import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  message,
  Row,
  Col,
  Typography,
  Space,
  Statistic,
  Tag,
  Divider,
  theme,
  Spin,
  Switch,
  Alert,
} from 'antd';
import {
  ApiOutlined,
  RobotOutlined,
  SendOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import { modelSelectionApi, userPreferencesApi, authApi, apiKeyApi } from '../../services/api';
import { 
  ModelOption, 
  GenerateTextRequest, 
  UserModelPreferences,
  ApiKey
} from '../../types';
import { usePermissionCheck, PermissionGuard } from '../../context/PermissionContext';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const ApiTest: React.FC = () => {
  const { token } = theme.useToken();
  const [textForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<string>('');
  const [balance, setBalance] = useState<number>(0);
  const [supportedModels, setSupportedModels] = useState<ModelOption[]>([]);
  const [userPreferences, setUserPreferences] = useState<UserModelPreferences | null>(null);
  const [currentApiKey, setCurrentApiKey] = useState<string>('');
  const [apiKeyStatus, setApiKeyStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [userApiKeys, setUserApiKeys] = useState<ApiKey[]>([]);

  // 权限检查
  const { 
    canViewApiTest,
    canViewApiTestBalance,
    canViewApiTestModels,
    canManageApiTestKey,
    canGenerateText,
    canSetDefaultModel
  } = usePermissionCheck();

  // 获取当前用户API密钥
  const fetchCurrentUserApiKey = async () => {
    try {
      setApiKeyStatus('loading');
      
      // 1. 获取当前用户信息
      const currentUserResponse = await authApi.getCurrentUser();
      const currentUser = currentUserResponse.data;
      if (!currentUser?.id) {
        throw new Error('无法获取当前用户信息');
      }

      // 2. 使用用户专用的智能获取接口
      try {
        const apiResponse = await apiKeyApi.getOrCreateMyApiKey();
        const selectedApi = apiResponse.data;
        
        setCurrentApiKey(selectedApi.api_key);
        setApiKeyStatus('ready');
        
        // 获取用户的完整API密钥列表用于显示统计信息
        const apiKeysResponse = await apiKeyApi.getUserApiKeys(currentUser.id);
        const apiKeys = Array.isArray(apiKeysResponse.data?.apis) ? apiKeysResponse.data.apis : [];
        setUserApiKeys(apiKeys);
        
        return; // 成功则直接返回
      } catch (userApiError) {
        console.log('用户智能获取接口调用失败，回退到手动选择模式:', userApiError);
      }

      // 3. 回退方案：手动获取和选择API密钥
      const apiKeysResponse = await apiKeyApi.getUserApiKeys(currentUser.id);
      const apiKeys = Array.isArray(apiKeysResponse.data?.apis) ? apiKeysResponse.data.apis : [];
      setUserApiKeys(apiKeys);

      // 优先选择启用的密钥
      let selectedKey = apiKeys.find((key: ApiKey) => key.is_active);
      
      // 如果没有启用的密钥，使用第一个密钥
      if (!selectedKey && apiKeys.length > 0) {
        selectedKey = apiKeys[0];
      }
      
      if (selectedKey) {
        setCurrentApiKey(selectedKey.api_key);
        setApiKeyStatus('ready');
      } else {
        // 只有在用户没有任何API密钥时才尝试创建
        if (apiKeys.length === 0) {
          try {
            const newApiKeyResponse = await apiKeyApi.createApiKey(currentUser.id, {
              name: 'API测试专用密钥',
              description: '用于API测试页面的自动生成密钥',
            });
            
            setCurrentApiKey(newApiKeyResponse.data.api_key);
            setApiKeyStatus('ready');
            
            // 刷新API密钥列表
            const updatedKeysResponse = await apiKeyApi.getUserApiKeys(currentUser.id);
            const updatedKeys = Array.isArray(updatedKeysResponse.data?.apis) ? updatedKeysResponse.data.apis : [];
            setUserApiKeys(updatedKeys);
          } catch (createError) {
            console.error('创建API密钥失败:', createError);
            setApiKeyStatus('error');
            message.error('无法创建API密钥，请联系管理员或到API密钥管理页面手动创建');
          }
        } else {
          // 如果有密钥但都不可用，提示用户
          setApiKeyStatus('error');
          message.warning('您的API密钥可能已被禁用，请到API密钥管理页面检查');
        }
      }
      
    } catch (error) {
      console.error('获取API密钥失败:', error);
      setApiKeyStatus('error');
      message.error('获取API密钥失败，请检查网络连接或联系管理员');
    }
  };

  useEffect(() => {
    fetchSupportedModels();
    fetchUserPreferences();
    fetchCurrentUserApiKey();
  }, []);

  // 当API密钥变化时，重新获取余额
  useEffect(() => {
    if (currentApiKey) {
      fetchBalance();
    }
  }, [currentApiKey]);

  const fetchBalance = async () => {
    if (!currentApiKey) {
      console.log('API密钥未就绪，无法获取余额');
      return;
    }

    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://apilink.haotunet.com';
      const response = await fetch(`${baseUrl}/api/v1/balance`, {
        method: 'GET',
        headers: {
          'X-API-Key': currentApiKey,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBalance(data.balance || 0);
      } else {
        console.error('获取余额失败:', response.status, response.statusText);
        setBalance(0);
      }
    } catch (error) {
      console.error('获取余额失败:', error);
      setBalance(0);
    }
  };

  const fetchSupportedModels = async () => {
    try {
      const modelsResponse = await modelSelectionApi.getSupportedModels();
      setSupportedModels(modelsResponse.data.models || []);
    } catch (error) {
      console.error('获取支持的模型失败:', error);
    }
  };

  const fetchUserPreferences = async () => {
    try {
      const preferencesResponse = await userPreferencesApi.getMyPreferences();
      setUserPreferences(preferencesResponse.data.data || null);
    } catch (error) {
      console.error('获取用户偏好失败:', error);
    }
  };

  const getModelInfo = (modelName: string) => {
    return supportedModels.find(m => m.name === modelName);
  };

  const onTextFinish = async (values: GenerateTextRequest) => {
    if (!currentApiKey) {
      message.error('API密钥未就绪，请稍候重试');
      return;
    }

    setLoading(true);
    setResponse('');
    
    try {
      // 使用API Key调用流式接口
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://apilink.haotunet.com';
      const url = `${baseUrl}/api/v1/generate?prompt=${encodeURIComponent(values.prompt)}&model_name=${encodeURIComponent(values.model_name || '')}&stream=true`;
      const urlWithApiKey = `${url}&api_key=${encodeURIComponent(currentApiKey)}`;

      // 使用 EventSource
      const eventSource = new EventSource(urlWithApiKey);
      
      eventSource.onmessage = (event) => {
        if (event.data === '[DONE]') {
          eventSource.close();
          setLoading(false);
          return;
        }
        
        setResponse(prev => prev + event.data);
      };
      
      eventSource.onerror = () => {
        eventSource.close();
        setLoading(false);
        message.error('EventSource 发生错误，连接已断开');
      };
      
      // 刷新余额
      setTimeout(() => {
        if (currentApiKey) {
          fetchBalance();
        }
      }, 1000);
      
    } catch (error) {
      setLoading(false);
      message.error('生成文本失败: ' + (error as any)?.message);
    }
  };

  // 权限检查
  if (!canViewApiTest()) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="访问受限"
          description="您没有权限访问API测试页面，请联系管理员申请权限。"
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', minHeight: '100vh', backgroundColor: token.colorBgLayout }}>
      <Title level={2} style={{ marginBottom: '8px', color: token.colorText }}>
        <ApiOutlined style={{ marginRight: '8px', color: token.colorPrimary }} />
        API 测试
      </Title>
      <Paragraph style={{ marginBottom: '24px', color: token.colorTextSecondary }}>
        测试和验证AI文本生成功能
      </Paragraph>

      {/* 账户信息 */}
      <Card size="small" style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <PermissionGuard permission="api_test.view_balance">
            <Col span={6}>
              <Statistic
                title="账户余额"
                value={balance}
                prefix="¥"
                precision={4}
              />
            </Col>
          </PermissionGuard>
          <PermissionGuard permission="api_test.view_models">
            <Col span={6}>
              <Statistic
                title="可用模型"
                value={supportedModels.filter(m => m.is_enabled).length}
                suffix={`/ ${supportedModels.length}`}
              />
            </Col>
          </PermissionGuard>
        </Row>
        
        <Divider style={{ margin: '16px 0 12px 0' }} />
        
        {/* API密钥状态显示 */}
        <PermissionGuard permission="api_test.manage_apikey">
          <Row gutter={16} align="middle">
            <Col span={12}>
              <Space>
                <KeyOutlined style={{ color: token.colorPrimary }} />
                <Text strong>API连接状态:</Text>
                {apiKeyStatus === 'loading' && (
                  <Space>
                    <Spin size="small" />
                    <Text type="secondary">正在获取API密钥...</Text>
                  </Space>
                )}
                {apiKeyStatus === 'ready' && (
                  <Space>
                    <Tag color="green">已连接</Tag>
                    <Text type="secondary">
                      使用密钥: {currentApiKey.substring(0, 20)}...
                    </Text>
                    {userApiKeys.length > 0 && (
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        (共{userApiKeys.length}个密钥)
                      </Text>
                    )}
                  </Space>
                )}
                {apiKeyStatus === 'error' && (
                  <Space>
                    <Tag color="red">连接失败</Tag>
                    <PermissionGuard permission="api_test.btn_retry">
                      <Button size="small" onClick={fetchCurrentUserApiKey}>重试</Button>
                    </PermissionGuard>
                  </Space>
                )}
              </Space>
            </Col>
          </Row>
        </PermissionGuard>
      </Card>

      {/* 文本生成 */}
      <PermissionGuard permission="api_test.generate_text">
        <Card 
          title={
            <Space>
              <RobotOutlined style={{ color: token.colorPrimary }} />
              <span>文本生成</span>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
        <Form
          form={textForm}
          layout="vertical"
          onFinish={onTextFinish}
        >
          <Form.Item
            label="模型选择"
            name="model_name"
            tooltip="选择用于生成文本的AI模型"
          >
            <Select
              placeholder="选择模型（留空使用默认模型）"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {supportedModels
                .filter(model => model.is_enabled)
                .map(model => (
                <Option key={model.name} value={model.name}>
                  <Space>
                    <Text strong>{model.display_name}</Text>
                    <Tag color="blue">{model.provider}</Tag>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      ¥输入:{model.input_price}/千Token | 输出:{model.output_price}/千Token
                    </Text>
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="提示文本"
            name="prompt"
            rules={[{ required: true, message: '请输入提示文本' }]}
          >
            <TextArea
              rows={4}
              placeholder="请输入您想要生成的文本内容的提示..."
              showCount
              maxLength={1000}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <PermissionGuard permission="api_test.btn_generate">
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  disabled={!currentApiKey || loading}
                  icon={<SendOutlined />}
                >
                  {!currentApiKey ? '等待API密钥...' : '生成文本'}
                </Button>
              </PermissionGuard>
            </Space>
          </Form.Item>
        </Form>

        {response && (
          <Card 
            size="small" 
            title="生成结果" 
            style={{ 
              marginTop: 16,
              backgroundColor: token.colorFillAlter,
              border: `1px solid ${token.colorBorder}`,
              borderRadius: token.borderRadius
            }}
          >
            <div style={{ 
              color: token.colorText,
              whiteSpace: 'pre-wrap',
              lineHeight: 1.6
            }}>
              {response}
            </div>
          </Card>
        )}
      </Card>
      </PermissionGuard>

      {/* Function Call 测试 */}
      <PermissionGuard permission="api_test.generate_text">
        <Card
          title={
            <Space>
              <RobotOutlined style={{ color: token.colorPrimary }} />
              <span>Function Call 测试</span>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <FunctionCallTest
            supportedModels={supportedModels}
            currentApiKey={currentApiKey}
            token={token}
          />
        </Card>
      </PermissionGuard>

      {/* 使用说明 */}
      <Card
        title={
          <Space>
            <InfoCircleOutlined style={{ color: token.colorInfo }} />
            <span>使用说明</span>
          </Space>
        }
        size="small"
        style={{
          backgroundColor: token.colorFillAlter,
          border: `1px solid ${token.colorBorder}`,
        }}
      >
        <Space direction="vertical" size="small">
          <Text>
            • <Text strong>文本生成:</Text> 输入提示文本，AI会基于您的输入生成相关内容
          </Text>
          <Text>
            • <Text strong>模型选择:</Text> 可以选择不同的AI模型，每个模型有不同的特点和定价
          </Text>
          <Text>
            • <Text strong>API密钥:</Text> 优先使用启用的密钥，无启用密钥时使用第一个密钥，无密钥时自动创建
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            注意：每次API调用都会消耗相应的余额，请注意账户余额情况
          </Text>
        </Space>
      </Card>
    </div>
  );
};

// Function Call 测试组件
const FunctionCallTest: React.FC<{
  supportedModels: ModelOption[];
  currentApiKey: string;
  token: any;
}> = ({ supportedModels, currentApiKey, token }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string>('');

  const onFinish = async (values: any) => {
    if (!currentApiKey) {
      message.error('API密钥未就绪，请稍候重试');
      return;
    }
    setLoading(true);
    setResult('');
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://apilink.haotunet.com';
      const url = `${baseUrl}/api/v1/function-call`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'X-API-Key': currentApiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          function_name: values.function_name,
          arguments: values.arguments,
          model_name: values.model_name,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setResult(JSON.stringify(data, null, 2));
      } else {
        setResult('调用失败: ' + response.statusText);
      }
    } catch (error) {
      setResult('调用异常: ' + (error as any)?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Form.Item
        label="模型选择"
        name="model_name"
        tooltip="选择用于Function Call的AI模型"
        rules={[{ required: true, message: '请选择模型' }]}
      >
        <Select
          placeholder="选择模型"
          allowClear
          showSearch
          optionFilterProp="children"
        >
          {supportedModels
            .filter(model => model.is_enabled)
            .map(model => (
              <Option key={model.name} value={model.name}>
                <Space>
                  <Text strong>{model.display_name}</Text>
                  <Tag color="blue">{model.provider}</Tag>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    ¥输入:{model.input_price}/千Token | 输出:{model.output_price}/千Token
                  </Text>
                </Space>
              </Option>
            ))}
        </Select>
      </Form.Item>
      <Form.Item
        label="函数名"
        name="function_name"
        rules={[{ required: true, message: '请输入函数名' }]}
      >
        <Input placeholder="如 get_weather" />
      </Form.Item>
      <Form.Item
        label="参数 (JSON格式)"
        name="arguments"
        rules={[{ required: true, message: '请输入参数' }]}
      >
        <Input.TextArea rows={3} placeholder='如 { "city": "北京" }' />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading} disabled={!currentApiKey || loading} icon={<SendOutlined />}>
          {!currentApiKey ? '等待API密钥...' : '调用Function Call'}
        </Button>
      </Form.Item>
      {result && (
        <Card
          size="small"
          title="调用结果"
          style={{
            backgroundColor: token.colorFillAlter,
            border: `1px solid ${token.colorBorder}`,
            borderRadius: token.borderRadius,
            marginTop: 16,
          }}
        >
          <pre style={{ color: token.colorText, fontSize: 13, margin: 0 }}>{result}</pre>
        </Card>
      )}
    </Form>
  );
};

export default ApiTest; 